import { useCallback, useEffect, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import type { Lista, Recurso, Profissional, Procedimento } from '../lib/api'
import { Secao, Vazio as VazioUI } from '../componentes/ui'
import { FichaProfissional } from './FichaProfissional'
import './config.css'

/**
 * As peças de cadastro da clínica.
 *
 * ─── ISTO ERA UMA TELA DE 540 LINHAS ──────────────────────────────
 *
 * `Config` era uma tela só com SEIS grupos — agenda, catálogo, equipe,
 * conversa, prontuário e conta — e quatro deles apenas re-renderizavam
 * telas que já tinham destino próprio na barra. A lista de partida do
 * Início mandava quatro dos cinco passos de montagem para `/ajustes`,
 * sem dizer para qual grupo: "cadastre quem atende" e "diga onde se
 * atende" caíam no mesmo lugar, e a pessoa tinha de caçar.
 *
 * O casco morreu. O que sobrou aqui são as PEÇAS, e cada uma é
 * montada por uma tela com endereço próprio:
 *
 *   AbaRecursos      → /salas
 *   AbaProcedimentos → /catalogo
 *   AbaProfissionais → /equipe
 *   SecaoGrade,
 *   SecaoJornadas    → /horarios   (em `ConfigSecoes.tsx`)
 *
 * Elas continuam recebendo `podeEditar` já resolvido por quem monta —
 * componente não decide permissão neste projeto.
 */

const brl = (c: number | string) =>
  (Number(c) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

/* ══ salas e recursos ═════════════════════════════════════════════ */

export function AbaRecursos({ podeEditar }: { podeEditar: boolean }) {
  const [itens, setItens] = useState<Recurso[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [form, setForm] = useState(false)

  const carregar = useCallback(async () => {
    try {
      const r = await api<Lista<Recurso>>('/v1/recursos?limite=100')
      setItens(r.itens)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar.')
    } finally { setCarregando(false) }
  }, [])
  useEffect(() => { void carregar() }, [carregar])

  async function alternar(r: Recurso) {
    try {
      await api(`/v1/recursos/${r.id}`, { metodo: 'PATCH', corpo: { ativo: !r.ativo } })
      await carregar()
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível alterar.')
    }
  }

  return (
    <section className="config__secao">
      <div className="config__cabeca">
        <p className="ajuda">
          Sala, equipamento, cadeira, maca. Capacidade maior que 1 é para
          hidroterapia, aula em grupo ou sala com várias macas.
        </p>
        {podeEditar && (
          <button className="botao" onClick={() => setForm(true)}>Nova sala</button>
        )}
      </div>

      {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

      {carregando ? <Esqueleto /> : itens.length === 0 ? (
        <Vazio texto="Nenhuma sala cadastrada. Sem pelo menos uma, a agenda não tem onde marcar." />
      ) : (
        <ul className="lista">
          {itens.map((r) => (
            <li key={r.id} className={`lista__item ${r.ativo ? '' : 'is-inativo'}`}>
              <div className="lista__principal">
                <span className="lista__nome">
                  {r.cor && <i className="lista__cor" style={{ background: r.cor }} />}
                  {r.nome}
                </span>
                <span className="ajuda">
                  {r.tipo.toLowerCase()}
                  {r.capacidade > 1 && ` · ${r.capacidade} lugares`}
                  {r.limpeza_min > 0 && ` · ${r.limpeza_min} min de limpeza`}
                  {r.setup_min > 0 && ` · ${r.setup_min} min de preparo`}
                </span>
              </div>
              {podeEditar && (
                <button className="botao botao--fantasma botao--pequeno"
                        onClick={() => void alternar(r)}>
                  {r.ativo ? 'Desativar' : 'Reativar'}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {form && (
        <FormRecurso aoFechar={() => setForm(false)}
                     aoSalvar={() => { setForm(false); void carregar() }} />
      )}
    </section>
  )
}

function FormRecurso({ aoFechar, aoSalvar }: { aoFechar(): void; aoSalvar(): void }) {
  const [nome, setNome] = useState('')
  const [tipo, setTipo] = useState('SALA')
  const [capacidade, setCapacidade] = useState(1)
  const [limpeza, setLimpeza] = useState(0)
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  async function submeter(e: React.FormEvent) {
    e.preventDefault(); setErro(null); setEnviando(true)
    try {
      await api('/v1/recursos', {
        metodo: 'POST',
        corpo: { nome: nome.trim(), tipo, capacidade, limpeza_min: limpeza },
      })
      aoSalvar()
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível salvar.')
      setEnviando(false)
    }
  }

  return (
    <Painel titulo="Nova sala ou recurso" aoFechar={aoFechar}>
      <form className="formulario" onSubmit={submeter}>
        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        <div className="campo">
          <label htmlFor="r-nome">Nome</label>
          <input id="r-nome" className="entrada" required autoFocus
                 placeholder="Sala 1, Ginásio, Cadeira 2…"
                 value={nome} onChange={(e) => setNome(e.target.value)} />
        </div>

        <div className="campo">
          <label htmlFor="r-tipo">Tipo</label>
          <select id="r-tipo" className="entrada" value={tipo}
                  onChange={(e) => setTipo(e.target.value)}>
            <option value="SALA">Sala</option>
            <option value="EQUIPAMENTO">Equipamento</option>
            <option value="CADEIRA">Cadeira</option>
            <option value="MACA">Maca</option>
          </select>
        </div>

        <div className="campo">
          <label htmlFor="r-cap">Quantas pessoas ao mesmo tempo</label>
          <input id="r-cap" className="entrada" type="number" min={1} max={50}
                 value={capacidade}
                 onChange={(e) => setCapacidade(Number(e.target.value))} />
          <span className="ajuda">
            1 para sala comum. Maior que 1 permite marcar atendimentos simultâneos
            no mesmo lugar.
          </span>
        </div>

        <div className="campo">
          <label htmlFor="r-limp">Limpeza entre atendimentos (min)</label>
          <input id="r-limp" className="entrada" type="number" min={0} max={240}
                 value={limpeza} onChange={(e) => setLimpeza(Number(e.target.value))} />
          <span className="ajuda">
            Esse tempo fica reservado depois de cada atendimento — ninguém consegue
            marcar em cima dele.
          </span>
        </div>

        <button className="botao" type="submit" disabled={enviando || !nome.trim()}>
          {enviando ? 'Salvando…' : 'Salvar'}
        </button>
      </form>
    </Painel>
  )
}

/* ══ profissionais ════════════════════════════════════════════════ */

export function AbaProfissionais({ podeEditar }: { podeEditar: boolean }) {
  const [itens, setItens] = useState<Profissional[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  // `null` = fechado · `'novo'` = cadastro · objeto = editando aquele
  const [ficha, setFicha] = useState<Profissional | 'novo' | null>(null)

  const carregar = useCallback(async () => {
    try {
      const r = await api<Lista<Profissional>>('/v1/profissionais?limite=100')
      setItens(r.itens)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar.')
    } finally { setCarregando(false) }
  }, [])
  useEffect(() => { void carregar() }, [carregar])

  return (
    <section className="config__secao">
      <div className="config__cabeca">
        <p className="ajuda">
          Quem atende. O conselho é opcional — esteticista e massoterapeuta não têm,
          e exigir um travaria metade das clínicas.
        </p>
        {podeEditar && (
          <button className="botao" onClick={() => setFicha('novo')}>Novo profissional</button>
        )}
      </div>

      {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

      {carregando ? <Esqueleto /> : itens.length === 0 ? (
        <Vazio texto="Nenhum profissional cadastrado." />
      ) : (
        <ul className="lista">
          {itens.map((p) => (
            <li key={p.id} className={`lista__item ${p.ativo ? '' : 'is-inativo'}`}>
              <div className="lista__principal">
                <span className="lista__nome">
                  {p.cor && <i className="lista__cor" style={{ background: p.cor }} />}
                  {p.nome}
                </span>
                <span className="ajuda">
                  {p.conselho === 'NENHUM'
                    ? 'sem conselho'
                    : `${p.conselho} ${p.conselho_numero ?? ''}${p.conselho_uf ? '/' + p.conselho_uf : ''}`}
                  {p.especialidades.length > 0 && ` · ${p.especialidades.join(', ')}`}
                </span>
              </div>

              {/* Os dois selos são independentes de propósito: atender e
                  ter login são coisas separadas. Quem vem duas vezes por
                  semana e não quer conta é agendável sem acesso. */}
              {p.tem_acesso
                ? <span className="selo selo--ok">tem acesso</span>
                : <span className="selo selo--neutro">sem login</span>}

              {/* Fisioterapia exige CREFITO para a evolução valer
                  (COFFITO 414/2012). Avisar aqui é mais barato do que
                  descobrir na auditoria. */}
              {p.conselho === 'NENHUM' && (
                <span className="selo selo--atencao">conselho pendente</span>
              )}
              {p.conselho !== 'NENHUM' && !p.cbo && (
                <span className="selo selo--atencao" title="Obrigatório na guia de convênio">
                  sem CBO
                </span>
              )}

              {podeEditar && (
                <button className="botao botao--fantasma botao--pequeno"
                        onClick={() => setFicha(p)}>Abrir ficha</button>
              )}
            </li>
          ))}
        </ul>
      )}

      {ficha && (
        <FichaProfissional
          profissional={ficha === 'novo' ? null : ficha}
          aoFechar={() => setFicha(null)}
          aoSalvar={async () => { setFicha(null); await carregar() }} />
      )}
    </section>
  )
}

/* O formulário antigo de profissional foi substituído por
   `FichaProfissional`, que cobre os mesmos campos e mais os que a guia
   de convênio, o recibo e o carimbo da evolução exigem. */


/* ══ procedimentos ════════════════════════════════════════════════ */

export function AbaProcedimentos({ podeEditar }: { podeEditar: boolean }) {
  const [itens, setItens] = useState<Procedimento[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  /* `false` fechado · `null` novo · objeto editando.
     `PATCH /v1/procedimentos/:id` existe e é testado desde a semana dos
     cadastros, e nenhuma tela chamava: um preço digitado errado era
     PERMANENTE, e a saída era criar um procedimento novo e desativar o
     velho — o que suja o relatório e quebra o histórico do que já foi
     marcado com ele. */
  const [form, setForm] = useState<false | null | Procedimento>(false)

  const carregar = useCallback(async () => {
    try {
      const r = await api<Lista<Procedimento>>('/v1/procedimentos?limite=100')
      setItens(r.itens)
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar.')
    } finally { setCarregando(false) }
  }, [])
  useEffect(() => { void carregar() }, [carregar])

  return (
    <section className="config__secao">
      <div className="config__cabeca">
        <p className="ajuda">
          O que a clínica faz, quanto dura e quanto custa. A duração é o que
          calcula o fim do atendimento sozinho.
        </p>
        {podeEditar && (
          <button className="botao" onClick={() => setForm(null)}>Novo procedimento</button>
        )}
      </div>

      {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

      {carregando ? <Esqueleto /> : itens.length === 0 ? (
        <Vazio texto="Nenhum procedimento cadastrado. Sem um, não dá para marcar atendimento." />
      ) : (
        <ul className="lista">
          {itens.map((p) => (
            <li key={p.id} className={`lista__item ${p.ativo ? '' : 'is-inativo'}`}>
              <div className="lista__principal">
                <span className="lista__nome">{p.nome}</span>
                <span className="ajuda">
                  {p.duracao_min} min
                  {p.limpeza_min > 0 && ` + ${p.limpeza_min} de limpeza`}
                  {' · '}{brl(p.valor_centavos)}
                  {p.comissao_tipo === 'PERCENTUAL' && ` · comissão ${p.comissao_valor}% (${brl(Math.round(Number(p.valor_centavos) * Number(p.comissao_valor) / 100))})`}
                  {p.comissao_tipo === 'FIXA' && ` · comissão ${brl(p.comissao_valor)}`}
                  {!p.ativo && ' · fora de uso'}
                </span>
              </div>
              {podeEditar && (
                <button className="botao botao--fantasma botao--pequeno"
                        onClick={() => setForm(p)}>Editar</button>
              )}
            </li>
          ))}
        </ul>
      )}

      {form !== false && (
        <FormProcedimento procedimento={form}
                          aoFechar={() => setForm(false)}
                          aoSalvar={() => { setForm(false); void carregar() }} />
      )}
    </section>
  )
}

/**
 * Novo procedimento — ou correção de um que já existe.
 *
 * `procedimento` nulo é criação; preenchido é edição. O mesmo
 * formulário serve aos dois porque os campos são os mesmos: manter um
 * "editar" separado é como um dos dois ganha um campo e o outro não.
 *
 * ⚠️ **Editar preço NÃO reescreve o que já foi cobrado.** O recebível
 * guarda o próprio valor no momento do lançamento; o procedimento é só
 * o padrão de quem vier depois. Está escrito na tela porque a dúvida
 * ("vai mudar o que já cobrei?") é a razão de ninguém corrigir preço.
 */
function FormProcedimento({ procedimento, aoFechar, aoSalvar }: {
  procedimento: Procedimento | null
  aoFechar(): void
  aoSalvar(): void
}) {
  const editando = procedimento !== null
  const [nome, setNome] = useState(procedimento?.nome ?? '')
  const [duracao, setDuracao] = useState(procedimento?.duracao_min ?? 50)
  const [limpeza, setLimpeza] = useState(procedimento?.limpeza_min ?? 0)
  const [valor, setValor] = useState(
    procedimento ? String(Number(procedimento.valor_centavos) / 100).replace('.', ',') : '')
  const [ativo, setAtivo] = useState(procedimento?.ativo ?? true)
  const [comTipo, setComTipo] = useState<'NENHUMA' | 'PERCENTUAL' | 'FIXA'>(
    procedimento?.comissao_tipo ?? 'NENHUMA')
  // PERCENTUAL guarda o %, FIXA guarda reais na tela (centavos no banco).
  const [comValor, setComValor] = useState(() => {
    if (!procedimento || procedimento.comissao_tipo === 'NENHUMA') return ''
    const v = Number(procedimento.comissao_valor)
    return procedimento.comissao_tipo === 'FIXA'
      ? (v / 100).toFixed(2).replace('.', ',') : String(v)
  })
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  // Digita-se em reais e guarda-se em centavos. Float em dinheiro é
  // como R$ 0,01 some do fechamento e ninguém acha de onde.
  const centavos = Math.round(Number(valor.replace(',', '.') || 0) * 100)

  async function submeter(e: React.FormEvent) {
    e.preventDefault(); setErro(null); setEnviando(true)
    try {
      // PERCENTUAL: o valor é o %. FIXA: reais viram centavos. NENHUMA: 0.
      const comissaoValor =
        comTipo === 'NENHUMA' ? 0
        : comTipo === 'FIXA'
          ? Math.round(Number(comValor.replace(/\./g, '').replace(',', '.') || 0) * 100)
          : Math.round(Number(comValor.replace(',', '.') || 0))
      const corpo = {
        nome: nome.trim(), duracao_min: duracao,
        limpeza_min: limpeza, valor_centavos: centavos,
        comissao_tipo: comTipo, comissao_valor: comissaoValor,
        ...(editando ? { ativo } : {}),
      }
      await api(
        editando ? `/v1/procedimentos/${procedimento.id}` : '/v1/procedimentos',
        { metodo: editando ? 'PATCH' : 'POST', corpo },
      )
      aoSalvar()
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível salvar.')
      setEnviando(false)
    }
  }

  return (
    <Painel titulo={editando ? 'Editar procedimento' : 'Novo procedimento'} aoFechar={aoFechar}>
      <form className="formulario" onSubmit={submeter}>
        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        <div className="campo">
          <label htmlFor="pc-nome">Nome</label>
          <input id="pc-nome" className="entrada" required autoFocus
                 placeholder="Sessão de fisioterapia"
                 value={nome} onChange={(e) => setNome(e.target.value)} />
        </div>

        <div className="config__dupla">
          <div className="campo">
            <label htmlFor="pc-dur">Duração (min)</label>
            <input id="pc-dur" className="entrada" type="number" min={5} max={600} required
                   value={duracao} onChange={(e) => setDuracao(Number(e.target.value))} />
          </div>
          <div className="campo">
            <label htmlFor="pc-limp">Limpeza (min)</label>
            <input id="pc-limp" className="entrada" type="number" min={0} max={240}
                   value={limpeza} onChange={(e) => setLimpeza(Number(e.target.value))} />
          </div>
        </div>

        <div className="campo">
          <label htmlFor="pc-valor">Valor (R$)</label>
          <input id="pc-valor" className="entrada" inputMode="decimal"
                 placeholder="180,00"
                 value={valor} onChange={(e) => setValor(e.target.value)} />
          {centavos > 0 && <span className="ajuda">{brl(centavos)}</span>}
          {editando && (
            <span className="ajuda">
              Muda só o que for cobrado daqui para a frente. O que já foi
              lançado guardou o próprio valor e não é reescrito.
            </span>
          )}
        </div>

        {/* ── comissão ────────────────────────────────────────────
            Sem isto, nenhum procedimento paga comissão e o Repasse é
            R$ 0,00 para todo mundo — e o dono não sabe por quê. O valor
            em reais fica à vista: "40" não diz nada; "= R$ 60 por
            atendimento" fecha a decisão. */}
        <div className="campo">
          <label htmlFor="pc-com">Comissão do profissional</label>
          <select id="pc-com" className="entrada" value={comTipo}
                  onChange={(e) => setComTipo(e.target.value as typeof comTipo)}>
            <option value="NENHUMA">Não paga comissão</option>
            <option value="PERCENTUAL">Percentual do valor</option>
            <option value="FIXA">Valor fixo por atendimento</option>
          </select>
        </div>

        {comTipo !== 'NENHUMA' && (
          <div className="campo pc-comissao">
            <label htmlFor="pc-comval">
              {comTipo === 'PERCENTUAL' ? 'Percentual (%)' : 'Valor fixo (R$)'}
            </label>
            <input id="pc-comval" className="entrada" inputMode="decimal"
                   placeholder={comTipo === 'PERCENTUAL' ? '40' : '60,00'}
                   value={comValor} onChange={(e) => setComValor(e.target.value)} />
            {comTipo === 'PERCENTUAL' && centavos > 0 && Number(comValor.replace(',', '.')) > 0 && (
              <span className="ajuda">
                = <strong>{brl(Math.round(centavos * Number(comValor.replace(',', '.')) / 100))}</strong> por atendimento
              </span>
            )}
            {comTipo === 'PERCENTUAL' && centavos === 0 && (
              <span className="ajuda">Preencha o valor acima para ver quanto isso dá em reais.</span>
            )}
          </div>
        )}

        {editando && (
          /* Desativar em vez de apagar: o procedimento está amarrado a
             agendamento e a recebível que já aconteceram. Apagá-lo
             deixaria histórico apontando para o nada. */
          <label className="config__caixa">
            <input type="checkbox" checked={ativo}
                   onChange={(e) => setAtivo(e.target.checked)} />
            <span>
              Ainda em uso
              <small className="ajuda">
                Desmarcado, some da hora de marcar — o que já foi marcado com
                ele continua no lugar.
              </small>
            </span>
          </label>
        )}

        <button className="botao" type="submit" disabled={enviando || !nome.trim()}>
          {enviando ? 'Salvando…' : 'Salvar'}
        </button>
      </form>
    </Painel>
  )
}

/* ══ peças ════════════════════════════════════════════════════════ */

function Painel({ titulo, aoFechar, children }: {
  titulo: string; aoFechar(): void; children: React.ReactNode
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') aoFechar() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [aoFechar])

  return (
    <>
      <div className="painel__fundo" onClick={aoFechar} />
      <aside className="painel" role="dialog" aria-modal="true" aria-label={titulo}>
        <header className="painel__topo">
          <h2>{titulo}</h2>
          <button className="botao botao--fantasma botao--pequeno" onClick={aoFechar}>
            Fechar
          </button>
        </header>
        {children}
      </aside>
    </>
  )
}

const Esqueleto = () => (
  <ul className="lista">
    {[0, 1, 2].map((i) => (
      <li key={i} className="lista__item">
        <div className="esqueleto" style={{ height: 34, width: '100%' }} />
      </li>
    ))}
  </ul>
)

const Vazio = ({ texto }: { texto: string }) => (
  <p className="config__vazio">{texto}</p>
)
